import type { AccountAsOf } from '../model/portfolioOverview'
import type {
  BadgeReason,
  MonthKey,
  ReasonAction,
  ReasonCode,
} from '../model/types'
import { countOf } from '../portfolioText'

// A pages_missing reason splits in two, since a file short only its last page is the common case.
export type ReviewKind = ReasonCode | 'last_page_missing'

// Listed in this order when two kinds have the same count.
const KIND_LABELS: Record<ReviewKind, string> = {
  missing_statement: 'no statement',
  failed_check: 'a total does not match the statement',
  unreadable_total: 'a printed total could not be extracted',
  table_not_extracted: 'a table on the statement was not extracted',
  not_checked: 'could not be checked',
  last_page_missing: 'last page not in the file',
  pages_missing: 'pages not in the file',
  opening_page_missing: 'opening holdings page not in the file',
  rows_dropped: 'some rows could not be extracted',
  cash_gap: 'cash does not match the printed balance',
  cash_not_captured: 'cash balance not captured',
  two_statements: 'two statements for one month',
  failed_read: 'could not be extracted',
  account_not_read: 'account not found in the statement',
  two_institutions: 'two institutions report the same account',
}

const KIND_ORDER = Object.keys(KIND_LABELS) as ReviewKind[]

export interface ReviewStatement {
  key: string
  accountId: string
  accountName: string
  firstMonth: MonthKey
  lastMonth: MonthKey
  monthCount: number
  action: ReasonAction
}

export interface ReviewGroup {
  kind: ReviewKind
  // Statements, or months for missing statements.
  count: number
  // "74 statements: last page not in the file"
  line: string
  statements: ReviewStatement[]
}

type ReviewedAccount = Pick<AccountAsOf, 'accountId' | 'name' | 'badge'>

const kindOf = (reason: BadgeReason): ReviewKind =>
  reason.code === 'pages_missing' && reason.lastPageOnly
    ? 'last_page_missing'
    : reason.code

const actionTarget = (action: ReasonAction) =>
  action.kind === 'open_statement' ? action.fileUrl : action.month

// One statement can give the same kind twice (two failed tables), but it is listed once.
const statementKey = (accountId: string, reason: BadgeReason) =>
  [accountId, reason.month, actionTarget(reason.action)].join('|')

const reviewStatementOf = (
  account: ReviewedAccount,
  reason: BadgeReason,
): ReviewStatement => {
  const months = reason.missingMonths ?? [reason.month]
  return {
    key: statementKey(account.accountId, reason),
    accountId: account.accountId,
    accountName: account.name,
    firstMonth: months[0],
    lastMonth: months[months.length - 1],
    monthCount: months.length,
    action: reason.action,
  }
}

const statementsByKind = (accounts: ReviewedAccount[]) => {
  const byKind = new Map<ReviewKind, Map<string, ReviewStatement>>()
  for (const account of accounts) {
    for (const reason of account.badge.reasons) {
      const kind = kindOf(reason)
      const statements = byKind.get(kind) ?? new Map()
      const statement = reviewStatementOf(account, reason)
      if (!statements.has(statement.key)) {
        statements.set(statement.key, statement)
      }
      byKind.set(kind, statements)
    }
  }
  return byKind
}

// Missing statements count in months, so a year with none reads "12 months", not one line.
const isCountedInMonths = (kind: ReviewKind) => kind === 'missing_statement'

const countedSize = (kind: ReviewKind, statements: ReviewStatement[]) =>
  isCountedInMonths(kind)
    ? statements.reduce((sum, statement) => sum + statement.monthCount, 0)
    : statements.length

const byAccountThenMonth = (first: ReviewStatement, second: ReviewStatement) =>
  first.accountName.localeCompare(second.accountName) ||
  first.firstMonth.localeCompare(second.firstMonth)

const groupOf = (
  kind: ReviewKind,
  statements: ReviewStatement[],
): ReviewGroup => {
  const count = countedSize(kind, statements)
  const unit = isCountedInMonths(kind) ? 'month' : 'statement'
  return {
    kind,
    count,
    line: `${countOf(count, unit)}: ${KIND_LABELS[kind]}`,
    statements: [...statements].sort(byAccountThenMonth),
  }
}

const biggestFirst = (first: ReviewGroup, second: ReviewGroup) =>
  second.count - first.count ||
  KIND_ORDER.indexOf(first.kind) - KIND_ORDER.indexOf(second.kind)

// Why the All accounts card needs review: each account's reasons as of the month, grouped by kind.
export const reviewGroups = (accounts: ReviewedAccount[]): ReviewGroup[] =>
  Array.from(statementsByKind(accounts), ([kind, statements]) =>
    groupOf(kind, Array.from(statements.values())),
  ).sort(biggestFirst)
